import { useMemo } from 'react';
import { Box, Paper, Typography, Chip, LinearProgress, Tooltip, Button } from '@mui/material';
import { PeopleOutlined, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { QuickAction } from '../../types/dashboard.types';
import { Employee } from '../../types/employee.types';
import { Shop } from '../../types/shop.types';
import { Schedule } from '../../types/schedule.types';
import { calculateEmployeeStats } from '../../utils/dashboardCalculations';

type ViewMode = 'daily' | 'weekly';

interface EmployeeStatisticsCardProps {
  rawData: {
    employees: Employee[];
    shops: Shop[];
    schedules: Schedule[];
  };
  viewMode: ViewMode;
  dateRange: string[];
  missingAvailabilityAction?: QuickAction;
}

export default function EmployeeStatisticsCard({
  rawData,
  viewMode,
  dateRange,
  missingAvailabilityAction,
}: EmployeeStatisticsCardProps) {
  const navigate = useNavigate();

  // Calculate stats based on selected date range
  const data = useMemo(
    () => calculateEmployeeStats(rawData.employees, rawData.shops, dateRange),
    [rawData.employees, rawData.shops, dateRange]
  );

  const availabilityTotal =
    data.availabilityStatus.withAvailability +
    data.availabilityStatus.withoutAvailability +
    data.availabilityStatus.partialAvailability;

  const periodLabel = viewMode === 'daily' ? "Today's" : "This Week's";

  return (
    <Paper
      sx={{
        p: 3,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: 'primary.light',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <PeopleOutlined color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Employee Statistics
        </Typography>
      </Box>

      {/* By Shop */}
      <Tooltip title="Number of active employees assigned to each shop" placement="top">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          By Shop
        </Typography>
      </Tooltip>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {data.byShop.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            No shops configured
          </Typography>
        ) : (
          data.byShop.map((shop) => (
            <Tooltip
              key={shop.shopId}
              title={`${shop.count} employee${shop.count !== 1 ? 's' : ''} assigned`}
              placement="top"
            >
              <Chip
                label={`${shop.shopName}: ${shop.count}`}
                size="small"
                sx={{
                  borderLeft: `4px solid ${shop.shopColor}`,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              />
            </Tooltip>
          ))
        )}
      </Box>

      {/* Availability Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Tooltip title={`How many employees have set their availability for ${viewMode === 'daily' ? 'this day' : 'this week'}`} placement="top">
          <Typography variant="body2" color="text.secondary">
            {periodLabel} Availability
          </Typography>
        </Tooltip>
        {missingAvailabilityAction && data.availabilityStatus.withoutAvailability > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
            onClick={() => navigate(missingAvailabilityAction.actionPath)}
            sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
          >
            Fix
          </Button>
        )}
      </Box>
      {availabilityTotal === 0 ? (
        <Typography variant="body2" color="text.disabled">
          No active employees
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Tooltip
            title={
              data.availabilityStatus.withAvailability > 0
                ? data.availabilityStatus.withAvailabilityNames.join(', ')
                : `No employees have set availability for ${viewMode === 'daily' ? 'this day' : 'all days'}`
            }
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 80 }}>
                Complete
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(data.availabilityStatus.withAvailability / availabilityTotal) * 100}
                  color="success"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                {data.availabilityStatus.withAvailability}
              </Typography>
            </Box>
          </Tooltip>
          {viewMode === 'weekly' && (
            <Tooltip
              title={
                data.availabilityStatus.partialAvailability > 0
                  ? data.availabilityStatus.partialAvailabilityNames.join(', ')
                  : 'No employees have partial availability'
              }
              placement="top"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 80 }}>
                  Partial
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(data.availabilityStatus.partialAvailability / availabilityTotal) * 100}
                    color="warning"
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                  {data.availabilityStatus.partialAvailability}
                </Typography>
              </Box>
            </Tooltip>
          )}
          <Tooltip
            title={
              data.availabilityStatus.withoutAvailability > 0
                ? data.availabilityStatus.withoutAvailabilityNames.join(', ')
                : 'All employees have set their availability'
            }
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 80 }}>
                Missing
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={(data.availabilityStatus.withoutAvailability / availabilityTotal) * 100}
                  color="error"
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                {data.availabilityStatus.withoutAvailability}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Paper>
  );
}
